<template>
    <div class="time-picker form-control-container">
        <div class="form-control" :class="{'invalid': isInvalid}">
            <input
                ref="inputElement"
                type="text"
                data-testid="formField"
                :value="userValue"
                @input="handleValueChange"
                @keydown.enter="handleEnterKey"
                @keydown.esc="handleEscapeKey"
                @focus="handleFocus"
                @blur="handleBlur"
                data-lpignore="true"
            />
        </div>
    </div>
</template>

<script lang="ts">
import { DayTime } from '@/utilities/types/day-time';
import { computed, defineComponent, onMounted, PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n';

export default defineComponent({
    props: {
        default: {
            type: Object as PropType<DayTime>,
            default: new DayTime(0, 0, 0),
        },
        modelValue: {
            type: null as unknown as PropType<DayTime|null>,
            required: true,
        },
        allowSeconds: {
            type: Boolean,
            default: true,
        },
        max: {
            type: null as unknown as PropType<DayTime|string|null>,
            default: null,
        }
    },

    emits: {
        "update:modelValue": (payload: DayTime) => payload instanceof DayTime,
    },

    setup(props, {emit})
    {
        const i18n = useI18n();
        const inputElement = ref<InstanceType<typeof HTMLInputElement>>();

        const internalValue = computed<DayTime>(() => {
            if (!props.modelValue) {
                return props.default;
            }

            return props.modelValue;
        });
        const internalMax = computed<DayTime|null>(() => {
            if (!props.modelValue) {
                return null;
            }

            if (typeof props.max === "string") {
                return DayTime.makeFromString(props.max);
            }

            return props.max;
        });

        if (internalValue.value !== props.modelValue) {
            updateModelValue(props.default);
        }

        const userValue = ref(formatValue(internalValue.value));
        const isInvalid = ref(false);
        let oldValue = userValue.value;
        let userBeforeEditValue = userValue.value;

        // When the component is mounted to the DOM, perform an initial validation of the value
        onMounted(() => {
            updateFieldValidation(internalValue.value);
        });

        //
        // Various utility functions
        //

        function formatValue(value: DayTime) : string
        {
            const formattedHours = value.hours.toFixed(0).padStart(2, '0');
            const formattedMinutes = value.minutes.toFixed(0).padStart(2, '0');
            const formattedSeconds = value.seconds.toFixed(0).padStart(2, '0');

            if (props.allowSeconds) {
                return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
            }

            return `${formattedHours}:${formattedMinutes}`;
        }

        function restoreInputOldValue()
        {
            // Looks like a bug: when setting a value, trying to set the same oldValue twice
            // doesn't update the DOM anymore, even if userValue.value was modified by the
            // user. Setting another random value fixes it...
            userValue.value += " ";
            userValue.value = oldValue;
        }

        function forceInputValue(value: string)
        {
            oldValue = value;
            restoreInputOldValue();
        }

        function updateFieldValidation(value: DayTime)
        {
            if (!inputElement.value) {
                return;
            }

            if (internalMax.value !== null && value.numericValue() > internalMax.value.numericValue()) {
                isInvalid.value = true;
                inputElement.value.setCustomValidity(i18n.t("validationMessage.max"));
                return;
            }

            isInvalid.value = false;
            inputElement.value.setCustomValidity("");
        }

        function updateModelValue(value: DayTime)
        {
            emit("update:modelValue", value);
        }

        function getValidationPattern(): RegExp
        {
            if (props.allowSeconds) {
                return /^([0-9]*:){0,2}([0-9]+(\.([0-9]+)?)?)?$/;
            }

            return /^([0-9]*:){0,1}([0-9]+(\.([0-9]+)?)?)?$/;
        }

        //
        // Event handlers
        //

        function handleFocus()
        {
            userBeforeEditValue = userValue.value;

            // When the value is zero, the user probably wants to type a different number without clearing the field
            if (internalValue.value.hours === 0 && internalValue.value.minutes === 0 && internalValue.value.seconds === 0) {
                forceInputValue("");
            }
        }

        function handleValueChange(e: Event)
        {
            const input = e.target as HTMLInputElement|null;

            if (input === null) {
                return;
            }

            // Only allow the user to type a valid value
            if (!getValidationPattern().test(input.value)) {
                restoreInputOldValue();
                return;
            }

            // The typed value is valid and can be remembered.
            oldValue = input.value;
            userValue.value = oldValue;
        }

        function handleBlur()
        {
            // If the user tried to clear the field but didn't type a new value,
            // restore the value it had before it was edited.
            if (userValue.value === "") {
                forceInputValue(userBeforeEditValue);
                return;
            }

            // Complete with trailing ":0" if the user did not provide enough separators
            let stringValue = userValue.value;
            const missingParts = 3 - stringValue.split(":").length;
            stringValue += ":0".repeat(missingParts);
            const newValue = DayTime.makeFromString(stringValue);

            // Handle validation
            updateFieldValidation(newValue);

            forceInputValue(formatValue(newValue));

            // Emit the updated value
            updateModelValue(newValue);
        }

        function handleEnterKey()
        {
            // When the user hit the enter key, commit the typed value to the model (same as blur, but don't remove
            // the focus on the element, otherwise the parent form (if any) will not be submitted)
            handleBlur();
        }

        function handleEscapeKey(e: KeyboardEvent)
        {
            const input = e.target as HTMLInputElement|null;

            // Restore the value that was set before trying to edit it and un-focus the element
            if (input !== null) {
                forceInputValue(userBeforeEditValue);
                input.blur();
            }
        }

        return {
            inputElement,
            userValue,
            isInvalid,
            handleFocus,
            handleValueChange,
            handleBlur,
            handleEnterKey,
            handleEscapeKey,
        };
    },
})
</script>

<style lang="scss" scoped>

.time-picker {
    display: flex;

    & > .form-control {
        display: flex;
        align-items: center;
        padding: 0 0.65rem 0 0;

        & > input {
            flex: 1;
            padding: 0.75rem 0.3rem 0.75rem 0.65rem;
            border: none !important;
            outline: none !important;
            text-align: right;
        }

        &:focus-within {
            padding: 0 calc(0.65rem - 1px) 0 0;
        }
        &:focus-within > input {
            padding: calc(0.75rem - 1px) 0.3rem calc(0.75rem - 1px) calc(0.65rem - 1px);
        }
    }
}

</style>
